//NOTE: https://orval.dev/reference/configuration/output#mutator
// use-custom-instance.ts

import { fetchAuthSession } from "aws-amplify/auth";
import { defaultStorage } from "aws-amplify/utils";
import Axios, { type AxiosError, type AxiosRequestConfig } from "axios";

export const AXIOS_INSTANCE = Axios.create({
	baseURL: import.meta.env.VITE_RECRUTA_AI_API_BASE_URL,
	// NOTE: デモのためにタイムアウトを設定していない
	// timeout: 30000,
});

const MAX_RETRY_COUNT = 3;

AXIOS_INSTANCE.interceptors.response.use(
	async (response) => response,
	async (error) => {
		if (error.response?.status === 401) {
			const retryCount = error.config?.retryCount || 0;

			// 想定外を考えてリトライ回数が最大値を超えた場合はログアウト
			if (retryCount >= MAX_RETRY_COUNT) {
				await defaultStorage.clear();
				window.location.href = "/login";
				return Promise.reject(error);
			}

			const newToken = await refreshToken();
			if (newToken) {
				// 失敗したリクエストを再試行
				const config = error.config;
				config.headers.Authorization = newToken;
				config.retryCount = retryCount + 1;
				return AXIOS_INSTANCE(config);
			}

			await defaultStorage.clear();
			window.location.href = "/login";
		}
		return Promise.reject(error);
	},
);

export const useCustomInstance = <T>(): ((
	config: AxiosRequestConfig,
) => Promise<T>) => {
	return async (config: AxiosRequestConfig) => {
		const token = await defaultStorage.getItem("jwt_token");
		const promise = AXIOS_INSTANCE({
			...config,
			headers: {
				Authorization: token ?? "",
			},
		}).then(({ data }) => data);

		return promise;
	};
};

const refreshToken = async (): Promise<string | undefined> => {
	const session = await fetchAuthSession();
	const newToken = session.tokens?.idToken?.toString();
	if (newToken) {
		await defaultStorage.removeItem("jwt_token");
		await defaultStorage.setItem("jwt_token", newToken);
	}
	return newToken;
};

type ErrorDetail = {
	message: string;
	locationType: string;
	location: string;
};

export type ErrorResponse = {
	error?: {
		code: number;
		message: string;
		details: ErrorDetail[];
	};
};

export type ErrorType<Error> = AxiosError<Error>;
